import { API, Auth } from "aws-amplify";
import {
  CreateAffiliatesRequest,
  CreatePromoCodeRequest,
  KYDAdminEvent,
  KYDEvent,
  KYDEventMetrics,
  UpdateAffiliateRequest,
  UpdatePromoCodeRequest,
  KYDPerformer,
  KYDPerformerDisplay,
  KYDGuestlistDisplayState,
  KYDEventBundle,
  UpdateTicketTypeAccessCodeRequest,
  CreateAccessCodeRequest,
  KYDAdminEventDetail,
  KYDGenre,
  KYDEventSummary,
  KYDAdminEventSummary,
  KYDPixelSettings,
  KYDStoryAsset,
  KYDStory,
  EventDetailPayoutsSectionData,
  KYDAdminEventDetailSection,
  KYDAdminEventDetailSectionsType,
  KYDReportDay,
} from "@common/interfaces/KYDEvent";
import { KYDCartV2 } from "@common/interfaces/KYDCart";
import {
  IKYDUser,
  KYDOrganizationUser,
  Wallet,
} from "@common/interfaces/KYDUser";
import {
  CreateTicketTypeRequest,
  GuestlistUserStatus,
} from "@common/interfaces/KYDTicket";
import { API_NAMES, getCurrentParamsUrl } from "../utils";
import { S3Response } from "react-s3-uploader";
import {
  KYDAdminFeed,
  KYDExternalLinkMetadata,
  KYDFeed,
  KYDFeedItem,
  KYDOrganizationEventLink,
  KYDOrganizationFull,
} from "@common/interfaces/KYDOrganization";
import { CognitoUser, CognitoAccessToken } from "amazon-cognito-identity-js";
import { ReportMode } from "../Manager/EventDetail/Nav/Reporting/ReportsForm";
import { KYDScope } from "@common/interfaces/ICognito";
import { PayoutsNavSectionData } from "src/Manager/EventDetail/Nav/Payouts/PayoutsNavSection";
import { PaymentReportPeriod, Period } from "@common/interfaces/KYDMetrics";

const fetchQueryParams = () => {
  if (window && window.location && window.location.search) {
    const params = new URLSearchParams(window.location.search);
    params.delete("update_id");
    const obj = Object.fromEntries(params);

    return obj;
  }
};

const admin_refreshAccessToken = async () => {
  console.log("Refresh access token");
  const currentUser = (await Auth.currentAuthenticatedUser()) as CognitoUser;
  const currentSession = await Auth.currentSession();
  const refreshToken = currentSession.getRefreshToken();

  const promise = new Promise((resolve, reject) => {
    currentUser.refreshSession(refreshToken, (err, session) => {
      console.log("Session: ", session);
      if (err) reject(err);
      else resolve(session);
    });
  });

  return promise;
};

const admin_fetchAccessToken = async (): Promise<CognitoAccessToken> => {
  const currentSession = await Auth.currentSession();
  const accessToken = currentSession.getAccessToken();

  return accessToken;
};

const admin_fetchMe = async (): Promise<KYDOrganizationUser> => {
  console.log("Fetch Me");
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.get(API_NAMES.ADMIN_ME, `/me`, {
      queryStringParameters,
    });

    if (response && response.updated_at) {
      const access_token = await admin_fetchAccessToken();
      if (
        access_token &&
        access_token["iat"] < new Date(response.updated_at).getTime() / 1000
      ) {
        await admin_refreshAccessToken();
      }
    }

    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_updateMe = async (
  organization_id: string
): Promise<KYDAdminEvent> => {
  console.log("Switch Org: ", organization_id);
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.put(API_NAMES.ADMIN_ME, `/me`, {
      body: { organization_id },
      queryStringParameters,
    });
    console.log("EV: ", response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const formatError = (err: any = {}) => {
  console.log(err.response);
  const { response = {} } = err;
  const { data = {}, status } = response;
  const { response: serverResponse = {} } = data;

  return {
    statusCode: status || 500,
    message: serverResponse.message || "An unknown error occured",
  };
};

const user_fetchEvent = async (event_id: string): Promise<KYDEvent> => {
  console.log("Fetch Event: ", event_id);
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.get(API_NAMES.EVENT, `/events/${event_id}`, {
      queryStringParameters,
    });
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const user_fetchBio = async (): Promise<KYDFeed> => {
  console.log("Fetch Bio");
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.get(API_NAMES.EVENT, `/bio`, {
      queryStringParameters,
    });
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const user_validatePaymentSession = async (url: string): Promise<KYDEvent> => {
  console.log("Validate Session: ", url);
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(API_NAMES.EVENT, `/payments`, {
      queryStringParameters,
      body: {
        type: "applypay",
        request: { url },
      },
    });
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_fetchEvent = async (event_id: string): Promise<KYDAdminEvent> => {
  console.log("Fetch Event: ", event_id);
  try {
    const queryStringParameters = fetchQueryParams();
    // HERE SECTION STUFF.
    const { response } = await API.get(API_NAMES.ADMIN, `/events/${event_id}`, {
      queryStringParameters,
    });
    console.log("EV: ", response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_fetchEventSections = async (
  event_id: string,
  types: string[]
): Promise<PayoutsNavSectionData> => {
  console.log("Fetch Event Sections: ", event_id);
  try {
    const { response } = await API.get(
      API_NAMES.ADMIN,
      `/events/${event_id}/sections`,
      {
        queryStringParameters: {
          types: types.join(","),
        },
      }
    );
    console.log("EV: ", response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};
const temp_transformEventToSections = (
  kydEvent: KYDAdminEvent
): KYDAdminEventDetail => {
  const commonData = {
    currency: kydEvent.currency,
    timezone: kydEvent.timezone,
    status: kydEvent.status,
    id: kydEvent.id,
    link: kydEvent.link,
    short_link: kydEvent.short_link,
  };
  return {
    ...kydEvent,
    nav: [
      {
        text: "Event",
        icon: "MdEvent",
        type: "link",
        url: `/events/${kydEvent.id}`,
        sections: [
          {
            type: "summary",
            permissions: ["read", "write"],
            data: {
              ...commonData,
              organization_id: kydEvent.organization_id,
              name: kydEvent.name,
              image: kydEvent.image,
              display_start_at: kydEvent.display_start_at,
              start_at: kydEvent.start_at,
              end_at: kydEvent.end_at,
              venue: kydEvent.venues.at(0),
              link: kydEvent.link,
              timezone: kydEvent.timezone,
              display_options: kydEvent.display_options,
              display_mode: kydEvent.display_mode,
              mapbox: {
                publishable_key:
                  kydEvent.display_options?.mapbox?.publishable_key,
              },
            },
          },
          { type: "ticket_sales", permissions: ["read"], data: commonData },
          {
            type: "timelines",
            permissions: ["read", "write"],
            data: {
              ...commonData,
              announce_at: kydEvent.announce_at,
              on_sale_at: kydEvent.on_sale_at,
              start_at: kydEvent.start_at,
              off_sale_at: kydEvent.off_sale_at,
              end_at: kydEvent.end_at,
              timezone: kydEvent.timezone,
              venue: kydEvent.venues.at(0),
            },
          },
          {
            type: "lineup",
            permissions: ["read", "write"],
            data: {
              ...commonData,
              performers: kydEvent.performers,
            },
          },
          {
            type: "description",
            permissions: ["read", "write"],
            data: {
              ...commonData,
              description: kydEvent.description,
            },
          },
          {
            type: "ticket_types",
            permissions: ["read", "write"],
            data: {
              ...commonData,
              hasTimelinesSet: !!(kydEvent.start_at && kydEvent.end_at),
              tiered_ticket_types: kydEvent.tiered_ticket_types,
              ticket_types: kydEvent.ticket_types,
            },
          },
          {
            type: "access_codes",
            permissions: ["read", "write"],
            data: {
              ...commonData,
              access_codes: kydEvent.access_codes,
              tiered_ticket_types: kydEvent.tiered_ticket_types,
              ticket_types: kydEvent.ticket_types,
            },
          },
          {
            type: "promo_codes",
            permissions: ["read", "write"],
            data: {
              ...commonData,
              tiered_ticket_types: kydEvent.tiered_ticket_types,
              ticket_types: kydEvent.ticket_types,
              promo_codes: kydEvent.promo_codes,
            },
          },
          {
            type: "affiliates",
            permissions: ["read", "write"],
            data: {
              ...commonData,
              affiliates: kydEvent.affiliates,
              affiliates_report_link: kydEvent.affiliates_report_link,
            },
          },
        ],
      },
      {
        text: "Guestlist",
        icon: "IoPeopleOutline",
        type: "link",
        url: `/events/${kydEvent.id}/guestlist`,
        sections: [
          {
            // should be split in more sections later
            type: "guestlist",
            data: {},
            permissions: ["read", "write"],
          },
        ],
      },
      {
        text: "Marketing",
        icon: "IoShareSocialOutline",
        type: "link",
        url: `/events/${kydEvent.id}/marketing`,
        sections: [
          {
            type: "summary",
            permissions: ["read"],
            data: {
              ...commonData,
              organization_id: kydEvent.organization_id,
              name: kydEvent.name,
              image: kydEvent.image,
              display_start_at: kydEvent.display_start_at,
              start_at: kydEvent.start_at,
              end_at: kydEvent.end_at,
              venue: kydEvent.venues.at(0),
              link: kydEvent.link,
              timezone: kydEvent.timezone,
              display_options: kydEvent.display_options,
              display_mode: kydEvent.display_mode,
              mapbox: {
                publishable_key:
                  kydEvent.display_options?.mapbox?.publishable_key,
              },
            },
          },
          {
            type: "marketing_links",
            permissions: ["read"],
            data: {
              ...commonData,
              organization: {
                slug_id: kydEvent.organization.slug_id,
              },
            },
          },
          {
            permissions: ["read"],
            data: {
              ...commonData,
              campaigns: kydEvent.campaigns,
            },
            type: "campaigns",
          },
        ],
      },
      {
        text: "Reporting",
        icon: "IoBarChartOutline",
        type: "link",
        url: `/events/${kydEvent.id}/reporting`,
        sections: [],
      },
      {
        text: "Settings",
        icon: "IoSettingsOutline",
        type: "link",
        url: `/events/${kydEvent.id}/settings`,
        sections: [],
      },
      {
        text: "Payouts",
        icon: "IoCashOutline",
        type: "link",
        url: `/events/${kydEvent.id}/payouts`,
        sections: [],
      },
    ],
  };
};

const admin_fetchEventWithSections = async (
  event_id: string
): Promise<KYDAdminEvent> => {
  try {
    const event = await admin_fetchEvent(event_id);

    if (!event) {
      throw formatError({
        message: "Event fetch failed",
      });
    }

    return temp_transformEventToSections(event);
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_createPromoCode = async (
  event_id: string,
  request: CreatePromoCodeRequest
): Promise<KYDAdminEvent> => {
  console.log("Fetch Event: ", event_id);
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/events/${event_id}/codes`,
      {
        body: request,
        queryStringParameters,
      }
    );
    console.log("EV: ", response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_createAccessCode = async (
  event_id: string,
  request: CreateAccessCodeRequest
) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/events/${event_id}/access_codes`,
      {
        body: request,
        queryStringParameters,
      }
    );
    console.log("EV: ", response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_updateAccessCode = async (
  event_id: string,
  access_code_id: string,
  request: UpdateTicketTypeAccessCodeRequest
) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.put(
      API_NAMES.ADMIN,
      `/events/${event_id}/access_codes/${access_code_id}`,
      {
        body: request,
        queryStringParameters,
      }
    );
    console.log("EV: ", response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_createSystemTicketType = async (
  event_id: string,
  request: CreateTicketTypeRequest
) => {
  console.log("Fetch Event: ", event_id);
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/events/${event_id}/system_ticket_types`,
      {
        body: request,
        queryStringParameters,
      }
    );
    console.log("EV: ", response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_createTicketType = async (
  event_id: string,
  request: CreateTicketTypeRequest
) => {
  console.log("Fetch Event: ", event_id);
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/events/${event_id}/ticket_types`,
      {
        body: request,
        queryStringParameters,
      }
    );
    console.log("EV: ", response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_createAffiliates = async (
  event_id: string,
  request: CreateAffiliatesRequest
): Promise<KYDAdminEvent> => {
  console.log("Fetch Event: ", event_id);
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/events/${event_id}/affiliates`,
      {
        body: request,
        queryStringParameters,
      }
    );
    console.log("EV: ", response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_updatePromoCode = async (
  event_id: string,
  promo_code_id: string,
  request: UpdatePromoCodeRequest
): Promise<KYDAdminEvent> => {
  console.log("Fetch Event: ", event_id);
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.put(
      API_NAMES.ADMIN,
      `/events/${event_id}/codes/${promo_code_id}`,
      {
        body: request,
        queryStringParameters,
      }
    );
    console.log("EV: ", response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_updateAffiliate = async (
  event_id: string,
  affiliate_id: string,
  request: UpdateAffiliateRequest
): Promise<KYDAdminEvent> => {
  console.log("Fetch Event: ", event_id);
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.put(
      API_NAMES.ADMIN,
      `/events/${event_id}/affiliates/${affiliate_id}`,
      {
        body: request,
        queryStringParameters,
      }
    );
    console.log("EV: ", response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_updateUserScopes = async (user_id: string, scopes: KYDScope[]) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.put(API_NAMES.ADMIN, `/users/${user_id}`, {
      body: { scopes },
      queryStringParameters,
    });
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_updateQRCode = async (
  qr_code_id: string,
  event_id: string
): Promise<KYDOrganizationFull> => {
  console.log("Update QR Code: ", qr_code_id);
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.put(
      API_NAMES.ADMIN,
      `/links/${qr_code_id}`,
      {
        body: {
          event_id,
        },
        queryStringParameters,
      }
    );
    console.log("EV: ", response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_createUploadUrl = async (
  file: File,
  callback: (params: S3Response) => void | Promise<void | string>
) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(API_NAMES.ADMIN, `/upload`, {
      queryStringParameters,
      body: {
        filename: file.name,
      },
    });
    console.log("EV: ", response);
    return callback(response);
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_createEventStory = async (
  event_id: string,
  file_key: string
): Promise<{ story: KYDStory }> => {
  try {
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/events/${event_id}/story`,
      {
        body: {
          file_key,
        },
      }
    );
    console.log("EV: ", response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_updateEventStory = async (
  event_id: string,
  assets: KYDStoryAsset[]
) => {
  try {
    const { response } = await API.put(
      API_NAMES.ADMIN,
      `/events/${event_id}/story`,
      {
        body: {
          assets,
        },
      }
    );
    console.log("EV: ", response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_fetchEventUpdate = async (
  event_id: string,
  update_id: string
): Promise<any> => {
  try {
    const { response } = await API.get(API_NAMES.ADMIN, `/events/${event_id}`, {
      queryStringParameters: {
        update_id,
      },
    });
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_fetchOrganization = async (): Promise<KYDOrganizationFull> => {
  console.log("Fetch Organization");
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.get(API_NAMES.ADMIN, `/organizations/me`, {
      queryStringParameters,
    });
    console.log("EV: ", response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_fetchBio = async (): Promise<KYDAdminFeed> => {
  console.log("Fetch Organization");
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.get(
      API_NAMES.ADMIN,
      `/organizations/me/bio`,
      {
        queryStringParameters,
      }
    );
    console.log("EV: ", response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const fetchCart = async (event_id: string): Promise<KYDCartV2> => {
  console.log("Fetch Event: ", event_id);
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.get(
      API_NAMES.EVENT,
      `/events/${event_id}/cart`,
      {
        queryStringParameters,
      }
    );
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const fetchEventWallet = async (event_id: string): Promise<Wallet> => {
  console.log("Fetch Event Wallet: ", event_id);
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.get(
      API_NAMES.EVENT,
      `/events/${event_id}/wallet`,
      { queryStringParameters }
    );
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw err;
  }
};

const updateCart = async (event_id: string, cart: any): Promise<any> => {
  console.log("Update Cart: ", event_id);
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.put(
      API_NAMES.EVENT,
      `/events/${event_id}/cart`,
      {
        body: { cart },
        queryStringParameters,
      }
    );
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const clearCart = async (event_id: string, expiration_token: string) => {
  console.log("Update Cart: ", event_id);
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.EVENT,
      `/events/${event_id}/cart/clear`,
      {
        body: { expiration_token },
        queryStringParameters,
      }
    );
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_fetchEvents = async (): Promise<{
  events: KYDAdminEventSummary[];
}> => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.get(API_NAMES.ADMIN, `/events`, {
      queryStringParameters,
    });

    if (Array.isArray(response)) {
      return {
        events: response,
      };
    }

    return {
      events: response?.events,
    };
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_fetchAlgoliaConfig = async (): Promise<{
  events_config?: {
    app_id: string;
    api_key: string;
    index_name: string;
  };
}> => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.get(API_NAMES.ADMIN, `/algolia`, {
      queryStringParameters,
    });

    return {
      events_config: response?.events_config,
    };
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_fetchOrganizationUsers = async (): Promise<{
  users: KYDOrganizationUser[];
}> => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.get(
      API_NAMES.ADMIN,
      `/organizations/users`,
      {
        queryStringParameters,
      }
    );
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_fetchEventBundles = async (): Promise<KYDEventBundle[]> => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.get(API_NAMES.ADMIN, `/bundles`, {
      queryStringParameters,
    });
    return response.bundles;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_fetchEventBundle = async (
  bundle_id: string
): Promise<KYDEventBundle[]> => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.get(
      API_NAMES.ADMIN,
      `/bundles/${bundle_id}`,
      {
        queryStringParameters,
      }
    );
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_fetchEventMetrics = async (
  event_id: string,
  request?: { start?: string; end?: string }
): Promise<KYDEventMetrics> => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.get(
      API_NAMES.ADMIN,
      `/events/${event_id}/metrics`,
      {
        queryStringParameters: {
          ...queryStringParameters,
          ...(request || {}),
        },
      }
    );
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_updateEvent = async (eventId: string, key: string, value: any) => {
  console.log(`Update Event: ${key}, ${value}`);
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.put(API_NAMES.ADMIN, `/events/${eventId}`, {
      queryStringParameters,
      body: {
        request: {
          key,
          value,
        },
      },
    });
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_updateLink = async (linkId: string, request: any) => {
  console.log(`Update Link: ${linkId}, ${JSON.stringify(request)}`);
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.put(API_NAMES.ADMIN, `/organizations/me`, {
      queryStringParameters,
      body: {
        feed_item_id: linkId,
        ...request,
      },
    });
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_createLink = async (link: string, type: KYDFeedItem["type"]) => {
  console.log(`Create Link: ${link}, ${type}`);
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/organizations/me/feed`,
      {
        queryStringParameters,
        body: {
          link,
          type,
        },
      }
    );
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_launchEvent = async (eventId: string) => {
  console.log(`Launch Event: ${eventId}`);
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.put(API_NAMES.ADMIN, `/events/${eventId}`, {
      queryStringParameters,
      body: {
        request: {
          type: "launch",
        },
      },
    });
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_updateTicketType = async (
  eventId: string,
  ticketTypeId: string,
  request: any
) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.put(
      API_NAMES.ADMIN,
      `/events/${eventId}/ticket_types/${ticketTypeId}`,
      {
        queryStringParameters,
        body: {
          request,
        },
      }
    );
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_deleteTicketType = async (
  eventId: string,
  ticketTypeId: string
) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.del(
      API_NAMES.ADMIN,
      `/events/${eventId}/ticket_types/${ticketTypeId}`,
      {
        queryStringParameters,
      }
    );
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_updateOrganization = async (action: string, request: any) => {
  const urlsForActions = {
    settings: "settings",
    social: "social",
    feed_image: "image",
    create_link: "links",
    update_link: "links",
  };

  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.put(
      API_NAMES.ADMIN,
      `/organizations/me/bio/${urlsForActions[action]}`,
      {
        queryStringParameters,
        body: {
          action,
          request,
        },
      }
    );
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_createUser = async (request: any) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(API_NAMES.ADMIN, `/users`, {
      queryStringParameters,
      body: request,
    });
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_deleteUser = async (user_id: string) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.del(API_NAMES.ADMIN, `/users/${user_id}`, {
      queryStringParameters,
    });
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_createEvent = async (request: any) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(API_NAMES.ADMIN, `/events`, {
      queryStringParameters,
      body: {
        request,
      },
    });
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const analytics_trackEvents = async (payload: any) => {
  try {
    const { event_id } = getCurrentParamsUrl();
    const params = fetchQueryParams();
    const res = await API.post(API_NAMES.ANALYTICS, `/`, {
      queryStringParameters: {
        ...params,
        event_id,
      },
      body: payload,
    });
    return res;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_fetchLinkMetadata = async (
  link: string
): Promise<{
  og: KYDExternalLinkMetadata;
  metadata: KYDExternalLinkMetadata;
}> => {
  try {
    const { response } = await API.post(API_NAMES.ADMIN, `/bio/validate`, {
      body: {
        link,
      },
    });
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_festGuestlistUser = async ({ event_id, user_id }): Promise<any> => {
  try {
    const queryStringParameters = fetchQueryParams();
    console.log({ event_id, user_id });
    const { response } = await API.get(
      API_NAMES.ADMIN,
      `/events/${event_id}/guestlist/${user_id}`,
      {
        queryStringParameters,
      }
    );
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_createGuestlistUser = async ({
  event_id,
  phone_number,
  ticket_type_id,
  guestlist_first_name,
  guestlist_last_name,
  quantity,
  id,
  mode,
  tags,
  notes,
}): Promise<{
  user_id: string;
  user_name: string;
  phone_number: string;
  quantity: number;
  id: string;
  did_reserve: boolean;
  notes: string;
  tags: string[];
  statusCode?: number;
  message?: string;
  validation_result?: {
    is_valid?: boolean;
    message?: string;
  };
}> => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/events/${event_id}/guestlist/assign`,
      {
        queryStringParameters,
        body: {
          phone_number: phone_number?.startsWith("+")
            ? phone_number
            : phone_number
              ? `+${phone_number}`
              : undefined,
          ticket_type_id,
          first_name: guestlist_first_name,
          last_name: guestlist_last_name,
          quantity: parseInt(quantity),
          id,
          mode,
          notes,
          kyd_tags: tags,
        },
      }
    );
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_deleteGuestlistUser = async ({
  event_id,
  phone_number,
  ticket_type_id,
  user_id,
}: {
  event_id: string;
  phone_number: string;
  ticket_type_id: string;
  user_id?: string;
}): Promise<any> => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.del(
      API_NAMES.ADMIN,
      `/events/${event_id}/guestlist`,
      {
        queryStringParameters,
        body: {
          phone_number,
          ticket_type_id,
          id: user_id,
        },
      }
    );
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

export interface KYDUserWithGuestlist extends IKYDUser {
  guestlist: {
    assigned: number;
    ticket_type_id: string;
    status: GuestlistUserStatus;
  };
  notes: string;
  tags: string[];
}

const admin_updateGuestlistAllocation = async ({
  event_id,
  allocation,
}: {
  event_id: string;
  allocation: KYDGuestlistDisplayState;
}): Promise<any> => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/events/${event_id}/guestlist`,
      {
        queryStringParameters,
        body: { allocation },
      }
    );
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_fetchGuestlist = async (
  event_id: string
): Promise<{
  users: KYDUserWithGuestlist[];
  allocation: KYDGuestlistDisplayState;
}> => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.get(
      API_NAMES.ADMIN,
      `/events/${event_id}/guestlist`,
      {
        queryStringParameters,
      }
    );
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_searchPerformers = async (
  event_id: string,
  query: string
): Promise<{ items: KYDPerformer[] }> => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/events/${event_id}/performers/search`,
      {
        queryStringParameters,
        body: { query },
      }
    );
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_createPerformer = async (
  event_id: string,
  performer_id: string
): Promise<{ id: string }> => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/events/${event_id}/performers`,
      {
        queryStringParameters,
        body: { performer_id },
      }
    );
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_updatePerformers = async (
  event_id: string,
  performers: KYDPerformerDisplay[]
): Promise<{ id: string }> => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.put(
      API_NAMES.ADMIN,
      `/events/${event_id}/performers`,
      {
        queryStringParameters,
        body: {
          performer_updates: performers.map((p) => ({
            id: p.id,
            headliner: p.headliner,
          })),
        },
      }
    );
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_updateGenres = async (
  event_id: string,
  genres: { name: string; id?: string }[]
): Promise<{ id: string }> => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.put(
      API_NAMES.ADMIN,
      `/events/${event_id}/genres`,
      {
        queryStringParameters,
        body: {
          genre_updates: genres.map((p) => ({
            id: p.id,
            name: p.name,
          })),
        },
      }
    );
    console.log(response);

    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_deletePerformer = async (
  event_id: string,
  performer_id: string
): Promise<{ id: string }> => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.del(
      API_NAMES.ADMIN,
      `/events/${event_id}/performers/${performer_id}`,
      {
        queryStringParameters,
      }
    );
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_cancelEvent = async (event_id: string) => {
  console.log("Cancel event", event_id);
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/events/${event_id}/cancel`,
      {
        queryStringParameters,
      }
    );
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_deleteEvent = async (event_id: string) => {
  console.log("Delete event", event_id);
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/events/${event_id}/archive`,
      {
        queryStringParameters,
      }
    );
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_updateBreakeven = async (
  event_id: string,
  breakeven_amount: number
) => {
  console.log("Update Breakeven: ", breakeven_amount);
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.put(
      API_NAMES.ADMIN,
      `/events/${event_id}/settings`,
      {
        queryStringParameters,
        body: { breakeven_amount },
      }
    );
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_updateEventSync = async (
  event_id: string,
  action: "ticket_type_display_order",
  data: any
) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.put(
      API_NAMES.ADMIN,
      `/events/${event_id}/sync`,
      {
        queryStringParameters,
        body: { action, ...data },
      }
    );
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_fetchFileColumns = async (event_id: string, fileKey: string) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/events/${event_id}/guestlist/import/sample`,
      {
        queryStringParameters,
        body: { file_key: fileKey },
      }
    );
    console.log(response);
    return response as {
      schema_fields: Array<{ is_match: boolean; column: string } | null>;
      sample: any[];
    };
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_guestlistImportCsv = async (
  event_id: string,
  fileKey: string,
  schema_fields: string[]
) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/events/${event_id}/guestlist/import`,
      {
        queryStringParameters,
        body: { file_key: fileKey, schema: schema_fields },
      }
    );
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_createReportRecipients = async ({
  event_id,
  mode,
  recipients,
  custom_days,
  report_time,
}: {
  event_id: string;
  mode: ReportMode;
  recipients: string[];
  custom_days?: KYDReportDay[];
  report_time?: string;
}) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/events/${event_id}/reports`,
      {
        queryStringParameters,
        body: { mode, recipients, custom_days, report_time },
      }
    );
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_createEventBundle = async ({
  data,
}: {
  data: {
    title: string;
  };
}) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(API_NAMES.ADMIN, `/bundles`, {
      queryStringParameters,
      body: data,
    });
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_updateEventBundle = async ({
  bundle_id,
  data,
}: {
  bundle_id: string;
  data: {
    title: string;
    events: string[];
  };
}) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.put(
      API_NAMES.ADMIN,
      `/bundles/${bundle_id}`,
      {
        queryStringParameters,
        body: data,
      }
    );
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_updateStripeConnection = async ({ code }: { code: string }) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.put(
      API_NAMES.ADMIN,
      `/organizations/stripe`,
      {
        queryStringParameters,
        body: { code },
      }
    );
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_updateLinkedOrganization = async (
  event_id: string,
  data: Partial<KYDOrganizationEventLink>
) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.put(
      API_NAMES.ADMIN,
      `/events/${event_id}/linked_organizations/${data.organization_id}`,
      {
        queryStringParameters,
        body: { organization_id: data.organization_id, scopes: data.scopes },
      }
    );
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_linkOrganization = async (
  event_id: string,
  data: Partial<KYDOrganizationEventLink>
) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/events/${event_id}/linked_organizations`,
      {
        queryStringParameters,
        body: { organization_id: data.organization_id, scopes: data.scopes },
      }
    );
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_disableOrganizationLinkForEvent = async (
  event_id: string,
  organization_id: string
) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.del(
      API_NAMES.ADMIN,
      `/events/${event_id}/linked_organizations/${organization_id}`,
      {
        queryStringParameters,
      }
    );
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_updateEventPixels = async ({
  event_id,
  pixels,
}: {
  event_id: string;
  pixels: KYDPixelSettings[];
}) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/events/${event_id}/pixels`,
      {
        queryStringParameters,
        body: { marketing_pixels: pixels },
      }
    );
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_resendReceipt = async (
  event_id: string,
  user_id: string,
  payment_id: string
) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/events/${event_id}/receipt`,
      {
        queryStringParameters,
        body: { user_id, payment_id },
      }
    );
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_fetchDashboard = async (
  period: "1d" | "7d" | "28d" | "3m" | "12m" | "mtd" | "qtd" | "ytd" = "1d"
) => {
  try {
    let queryStringParameters = fetchQueryParams() || {};
    queryStringParameters.period = period;

    const { response } = await API.get(API_NAMES.ADMIN, `/dashboard`, {
      queryStringParameters,
    });
    console.log(response);

    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_refundTickets = async (
  event_id: string,
  data: {
    tickets: {
      ticket_type_id: string;
      id: string;
    }[];
    retain_ticket: boolean;
  }
) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/events/${event_id}/refund_tickets`,
      {
        queryStringParameters,
        body: data,
      }
    );
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_duplicateEvent = async (
  event_id: string,
  newEventOverrides: { start_at: string }
) => {
  try {
    console.log("Duplicate event", event_id);
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/events/${event_id}/duplicate`,
      {
        queryStringParameters,
        body: newEventOverrides,
      }
    );
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_updateAttendeeMetadata = async (
  event_id: string,
  data: {
    id: string;
    notes: string;
    tags: string[];
  }
) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/events/${event_id}/attendees/${data.id}`,
      {
        queryStringParameters,
        body: data,
      }
    );

    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_fetchAttendeeDetail = async (event_id: string, attendee_id) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.get(
      API_NAMES.ADMIN,
      `/events/${event_id}/attendees/${attendee_id}`,
      {
        queryStringParameters,
      }
    );

    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_fillWaitlistOrders = async (
  event_id: string,
  data: {
    waitlist_ids: string[];
  }
) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/events/${event_id}/waitlists/fill`,
      {
        queryStringParameters,
        body: data,
      }
    );

    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_createAudience = async ({
  event_id,
  name,
  description,
}: {
  event_id: string;
  name: string;
  description: string;
}) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/events/${event_id}/ads/audiences`,
      {
        queryStringParameters,
        body: {
          event_id,
          name,
          description,
        },
      }
    );

    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};
const admin_updateCampaign = async ({
  event_id,
  id,
  ...body
}: {
  id: string;
  event_id: string;
  name: string;
  campaign_type: "conversion" | "awareness";
  start_time: string;
  stop_time: string;
  page_id: string;
  ad_account_id: string;
}) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.put(
      API_NAMES.ADMIN,
      `/events/${event_id}/ads/campaigns/${id}`,
      {
        queryStringParameters,
        body,
      }
    );

    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_createCampaign = async ({
  event_id,
  ...body
}: {
  id: string;
  event_id: string;
  name: string;
  campaign_type: "conversion" | "awareness";
  start_time: string;
  stop_time: string;
  page_id: string;
  ad_account_id: string;
}) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/events/${event_id}/ads/campaigns`,
      {
        queryStringParameters,
        body,
      }
    );

    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_previewAd = async ({
  event_id,
  adset_id,
  campaign_id,
  width,
  height,
}: {
  event_id: string;
  adset_id: string;
  campaign_id: string;
  width?: number;
  height?: number;
}) => {
  try {
    console.log({ event_id, campaign_id, adset_id });
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/events/${event_id}/ads/${campaign_id}/adsets/${adset_id}/preview`,
      {
        queryStringParameters,
        body: {
          // // INSTAGRAM_STORY or INSTAGRAM_STANDARD
          // ad_format: "INSTAGRAM_STANDARD",
          width,
          height,
        },
      }
    );

    console.log(response);
    return response as {
      previews: {
        format: string;
        name: string;
        preview: {
          body: string;
        };
      }[];
    };
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_updateAdCreative = async ({
  event_id,
  campaign_id,
  adset_id,
  ...body
}: CreateAdCreativeRequest) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.put(
      API_NAMES.ADMIN,
      `/events/${event_id}/ads/${campaign_id}/adsets/${adset_id}/adcreatives`,
      {
        queryStringParameters,
        body,
      }
    );

    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_createAdset = async ({
  event_id,
  campaign_id,
  ...body
}: CreateAdsetRequest) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/events/${event_id}/ads/${campaign_id}/adsets`,
      {
        queryStringParameters,
        body,
      }
    );

    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

export interface Ad {
  id?: string;
  name?: string;
  external_id?: string;
  creative?: {
    ad_formats?: string[];
    id?: string;
    message?: string;
    description?: string;
    external_id?: string;
    page_id?: string;
    instagram_actor_id?: string;
    images?: {
      hash?: string;
      picture_url?: string;

      fileKey?: string;

      name?: string;
      description?: string;
    }[];
    videos?: {
      id?: string;
      external_id?: string;

      fileKey?: string;

      thumbnail_url?: string;
      source?: string;
      type?: string;
    }[];
    variations?: {
      images?: {
        hash?: string;
        picture_url?: string;
        fileKey?: string;
        name?: string;
        type?: string;
        description?: string;
      }[];
      videos?: {
        id?: string;
        external_id?: string;
        thumbnail_url?: string;
        source?: string;
        fileKey?: string;
        type?: string;
      }[];
      titles?: string[];
      descriptions?: string[];
      rules?: {
        priority: number;
        customization: {
          publisher_platforms?: string[];
          facebook_positions?: string[];
          instagram_positions?: string[];
          device_platforms?: string[];
          messenger_positions?: string[];
          audience_network_positions?: string[];
        }[];
      }[];
    };
  };
}
export interface AdRequest {
  id?: string;
  event_id: string;
  campaign_id: string;
  status?: string;
  adset_id: string;
  external_id?: string;
  organization_id: string;
  creative: {
    id: string;
    ad_format: string;
    instagram_actor_id?: string;
    page_id?: string;
    external_id?: string;
    images: {
      square?: {
        hash?: string;
        picture_url?: string;
        fileKey?: string;
      }[];
      // rectangle?: {
      //   hash?: string;
      //   picture_url?: string;
      //   fileKey?: string;
      // }[];
      portrait?: {
        hash?: string;
        picture_url?: string;
        fileKey?: string;
      }[];
    };
    videos?: {
      square?: {
        fileKey?: string;
        thumbnail_url?: string;
        source?: string;
        external_id?: string;
      }[];
      portrait?: {
        fileKey?: string;
        thumbnail_url?: string;
        source?: string;
        external_id?: string;
      }[];
      // rectangle?: {
      //   fileKey?: string;
      //   thumbnail_url?: string;
      //   source?: string;
      //   external_id?: string;
      // }[];
    };
    message: string;
  };
}
export interface CreateAdCreativeRequest {
  event_id: string;
  campaign_id: string;
  adset_id: string;
  organization_id: string;
  ads: AdRequest[];
}

export interface CreateAdsetRequest {
  id?: string;
  external_id?: string;
  event_id: string;
  name: string;
  campaign_id: string;
  pixel_id: string;
  lifetime_budget: number;
  start_time: string;
  stop_time: string;
  optimization_goal: string;
  custom_audience_files: {
    file_key: string;
    name?: string;
    description?: string;
  }[];
  campaign_type: "conversion" | "awareness";
  audience_ids: string[];
  caption: string;
  age_min?: number;
  age_max?: number;
  countries: { key: string; name: string }[];
  geo_markets: { key: string; name: string }[];
  interests: { id: string; name: string }[];
  behaviors: { id: string; name: string }[];
  demographics: { id: string; name: string }[];
}

const admin_updateAdset = async ({
  event_id,
  campaign_id,
  id,
  ...body
}: CreateAdsetRequest) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.put(
      API_NAMES.ADMIN,
      `/events/${event_id}/ads/${campaign_id}/adsets/${id}`,
      {
        queryStringParameters,
        body,
      }
    );

    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_listCampaigns = async (event_id: string) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.get(
      API_NAMES.ADMIN,
      `/events/${event_id}/ads/campaigns`,
      {
        queryStringParameters,
      }
    );

    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_fetchCampaign = async (event_id: string, campaign_id: string) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.get(
      API_NAMES.ADMIN,
      `/events/${event_id}/ads/campaigns/${campaign_id}`,
      {
        queryStringParameters,
      }
    );

    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_checkCampaignUpdate = async (
  event_id: string,
  campaign_id: string,
  execution_id: string
): Promise<{
  status: string;
  update_status: string;
  id: string;
  date: string;
  request: any;
}> => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.get(
      API_NAMES.ADMIN,
      `/events/${event_id}/ads/campaigns/${campaign_id}/check`,
      {
        queryStringParameters: {
          ...queryStringParameters,
          execution_id,
        },
      }
    );

    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_listPixels = async (
  event_id: string,
  { ad_account_id }: { ad_account_id: string }
) => {
  try {
    const { response } = await API.get(
      API_NAMES.ADMIN,
      `/events/${event_id}/ads/pixels`,
      {
        queryStringParameters: {
          ad_account_id,
        },
      }
    );

    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};
const admin_listInstagramAccounts = async (
  event_id: string,
  { ad_account_id }: { ad_account_id: string }
) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.get(
      API_NAMES.ADMIN,
      `/events/${event_id}/ads/instagram_accounts`,
      {
        queryStringParameters: {
          ...queryStringParameters,
          ad_account_id,
        },
      }
    );

    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_listPages = async (
  event_id: string,
  { ad_account_id }: { ad_account_id: string }
) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.get(
      API_NAMES.ADMIN,
      `/events/${event_id}/ads/pages`,
      {
        queryStringParameters: {
          ...queryStringParameters,
          ad_account_id,
        },
      }
    );

    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_listAdAccounts = async (event_id: string) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.get(
      API_NAMES.ADMIN,
      `/events/${event_id}/ads/accounts`,
      {
        queryStringParameters,
      }
    );

    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_listCustomConversions = async (event_id: string) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.get(
      API_NAMES.ADMIN,
      `/events/${event_id}/ads/custom_conversions`,
      {
        queryStringParameters,
      }
    );

    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_listAdsets = async ({
  eventId,
  campaignId,
}: {
  eventId: string;
  campaignId: string;
}) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.get(
      API_NAMES.ADMIN,
      `/events/${eventId}/ads/${campaignId}/adsets`,
      {
        queryStringParameters,
      }
    );

    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_searchAudiences = async (
  event_id: string,
  { query, ad_account_id }: { query: string; ad_account_id: string }
) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/events/${event_id}/ads/search/custom-audiences`,
      {
        queryStringParameters,
        body: { query, ad_account_id },
      }
    );

    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_listAudiences = async (
  event_id: string,
  { ad_account_id }: { ad_account_id: string }
) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.get(
      API_NAMES.ADMIN,
      `/events/${event_id}/ads/audiences`,
      {
        queryStringParameters: {
          ad_account_id,
        },
      }
    );

    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};
const admin_listAdCreativesForAdset = async ({
  event_id,
  adset_id,
  campaign_id,
}: {
  event_id: string;
  campaign_id: string;
  adset_id: string;
}) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.get(
      API_NAMES.ADMIN,
      `/events/${event_id}/ads/${campaign_id}/adsets/${adset_id}/adcreatives`,
      {
        queryStringParameters,
      }
    );

    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_searchInterests = async ({
  event_id,
  query,
}: {
  event_id: string;
  query: string;
}) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/events/${event_id}/ads/search/interests`,
      {
        queryStringParameters,
        body: { query },
      }
    );

    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_searchBehaviors = async ({
  event_id,
}: {
  event_id: string;
  query: string;
}) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/events/${event_id}/ads/search/behaviors`,
      {
        queryStringParameters,
      }
    );

    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_searchDemographics = async ({
  event_id,
  query,
  subtype,
}: {
  event_id: string;
  query: string;
  subtype: string;
}) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/events/${event_id}/ads/search/demographics`,
      {
        queryStringParameters,
        body: { query, subtype },
      }
    );

    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_searchGeoMarkets = async ({
  event_id,
  query,
}: {
  event_id: string;
  query: string;
}) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/events/${event_id}/ads/search/locations`,
      {
        queryStringParameters,
        body: { query },
      }
    );

    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_searchCountries = async ({
  event_id,
  query,
}: {
  event_id: string;
  query: string;
}) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/events/${event_id}/ads/search/countries`,
      {
        queryStringParameters,
        body: { query },
      }
    );

    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_unpublishCampaign = async ({
  event_id,
  campaign_id,
}: {
  event_id: string;
  campaign_id: string;
}) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/events/${event_id}/ads/${campaign_id}/unpublish`,
      {
        queryStringParameters,
      }
    );

    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_publishCampaign = async ({
  event_id,
  campaign_id,
  publish_all_adsets = false,
}: {
  event_id: string;
  campaign_id: string;
  publish_all_adsets?: boolean;
}) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/events/${event_id}/ads/${campaign_id}/publish`,
      {
        queryStringParameters,
        body: {
          publish_all_adsets,
        },
      }
    );

    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_publishAdset = async ({
  event_id,
  campaign_id,
  adset_id,
}: {
  event_id: string;
  campaign_id: string;
  adset_id: string;
}) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/events/${event_id}/ads/${campaign_id}/adsets/${adset_id}/publish`,
      {
        queryStringParameters,
      }
    );

    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_unpublishAdset = async ({
  event_id,
  campaign_id,
  adset_id,
}: {
  event_id: string;
  campaign_id: string;
  adset_id: string;
}) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/events/${event_id}/ads/${campaign_id}/adsets/${adset_id}/unpublish`,
      {
        queryStringParameters,
      }
    );

    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_createOrganizationAccess = async ({ code }: { code: string }) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/organizations/me/ads/access`,
      {
        queryStringParameters,
        body: {
          code,
        },
      }
    );

    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_removeOrganizationAccess = async () => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.del(
      API_NAMES.ADMIN,
      `/organizations/me/ads/access`,
      {
        queryStringParameters,
      }
    );

    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_fetchCustomAudienceFileColumns = async ({ event_id, file_key }) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/events/${event_id}/ads/custom_audience_files/columns`,
      {
        queryStringParameters,
        body: {
          file_key,
        },
      }
    );

    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_fetchOrganizationPayouts = async ({
  period,
  start_at,
  end_at,
}: {
  period: PaymentReportPeriod;
  start_at: string;
  end_at: string;
}) => {
  try {
    const { response } = await API.get(
      API_NAMES.ADMIN,
      `/organizations/payouts`,
      {
        queryStringParameters: {
          period,
          start_at,
          end_at,
        },
      }
    );

    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_fetchSingleOrganizationPayout = async ({
  id,
  asCSV = false,
}: {
  id: string;
  asCSV?: boolean;
}) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.get(
      API_NAMES.ADMIN,
      `/organizations/payouts/${id}`,
      {
        queryStringParameters: {
          asCSV,
        },
      }
    );
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};
const admin_fetchPayoutDetailsReport = async ({
  payout_id,
}: {
  payout_id: string;
}) => {
  try {
    const { response } = await API.get(
      API_NAMES.ADMIN,
      `/organizations/payouts/${payout_id}/csv`,
      {
        queryStringParameters: {
          asCSV: true,
        },
      }
    );

    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const ADMIN = {
  fetchAlgoliaConfig: admin_fetchAlgoliaConfig,
  fetchEvents: admin_fetchEvents,
  fetchEvent: admin_fetchEvent,
  fetchEventWithSections: admin_fetchEvent,
  fetchEventUpdate: admin_fetchEventUpdate,
  updateEvent: admin_updateEvent,
  updateTicketType: admin_updateTicketType,
  fetchOrganization: admin_fetchOrganization,
  updateOrganization: admin_updateOrganization,
  createUploadUrl: admin_createUploadUrl,
  createPromoCode: admin_createPromoCode,
  updatePromoCode: admin_updatePromoCode,
  updateQRCode: admin_updateQRCode,
  createAffiliates: admin_createAffiliates,
  updateAffiliate: admin_updateAffiliate,
  createTicketType: admin_createTicketType,
  deleteTicketType: admin_deleteTicketType,
  launchEvent: admin_launchEvent,
  fetchBio: admin_fetchBio,
  updateLink: admin_updateLink,
  createLink: admin_createLink,
  fetchEventMetrics: admin_fetchEventMetrics,
  createEvent: admin_createEvent,
  fetchLinkMetadata: admin_fetchLinkMetadata,
  refreshAccessToken: admin_refreshAccessToken,
  fetchAccessToken: admin_fetchAccessToken,
  fetchMe: admin_fetchMe,
  updateMe: admin_updateMe,
  updateUserScopes: admin_updateUserScopes,
  createUser: admin_createUser,
  deleteUser: admin_deleteUser,
  fetchGuestlist: admin_fetchGuestlist,
  updateGuestlistAllocation: admin_updateGuestlistAllocation,
  deleteGuestlistUser: admin_deleteGuestlistUser,
  createGuestlistUser: admin_createGuestlistUser,
  fetchGuestlistUser: admin_festGuestlistUser,
  searchPerformers: admin_searchPerformers,
  createPerformer: admin_createPerformer,
  deletePerformer: admin_deletePerformer,
  updatePerformers: admin_updatePerformers,
  deleteEvent: admin_deleteEvent,
  cancelEvent: admin_cancelEvent,
  updateBreakeven: admin_updateBreakeven,
  guestlistImportCsv: admin_guestlistImportCsv,
  fetchFileColumns: admin_fetchFileColumns,
  createSystemTicketType: admin_createSystemTicketType,
  updateEventSync: admin_updateEventSync,
  createReportRecipients: admin_createReportRecipients,
  updateStripeConnection: admin_updateStripeConnection,
  fetchEventBundles: admin_fetchEventBundles,
  fetchEventBundle: admin_fetchEventBundle,
  createAccessCode: admin_createAccessCode,
  updateAccessCode: admin_updateAccessCode,
  linkOrganizationToEvent: admin_linkOrganization,
  disableOrganizationLinkForEvent: admin_disableOrganizationLinkForEvent,
  updateGenres: admin_updateGenres,
  createEventBundle: admin_createEventBundle,
  updateEventBundle: admin_updateEventBundle,
  refundTickets: admin_refundTickets,
  updateAttendeeMetadata: admin_updateAttendeeMetadata,
  updateEventPixels: admin_updateEventPixels,
  duplicateEvent: admin_duplicateEvent,
  updateLinkedOrganization: admin_updateLinkedOrganization,
  fetchOrganizationUsers: admin_fetchOrganizationUsers,
  fillWaitlistOrders: admin_fillWaitlistOrders,
  fetchAttendeeDetail: admin_fetchAttendeeDetail,
  createAudience: admin_createAudience,
  createCampaign: admin_createCampaign,
  createAdset: admin_createAdset,
  updateAdset: admin_updateAdset,
  listPixels: admin_listPixels,
  listPages: admin_listPages,
  listCustomConversions: admin_listCustomConversions,
  listInstagramAccounts: admin_listInstagramAccounts,
  previewAd: admin_previewAd,
  listAudiences: admin_listAudiences,
  searchInterests: admin_searchInterests,
  searchGeoMarkets: admin_searchGeoMarkets,
  publishCampaign: admin_publishCampaign,
  fetchCampaign: admin_fetchCampaign,
  publishAdset: admin_publishAdset,
  unpublishAdset: admin_unpublishAdset,
  listAdAccounts: admin_listAdAccounts,
  updateCampaign: admin_updateCampaign,
  checkCampaignUpdate: admin_checkCampaignUpdate,
  unpublishCampaign: admin_unpublishCampaign,
  updateAdCreative: admin_updateAdCreative,
  createOrganizationAccess: admin_createOrganizationAccess,
  removeOrganizationAccess: admin_removeOrganizationAccess,
  fetchCustomAudienceFileColumns: admin_fetchCustomAudienceFileColumns,
  searchAudiences: admin_searchAudiences,
  createEventStory: admin_createEventStory,
  updateEventStory: admin_updateEventStory,
  resendReceipt: admin_resendReceipt,
  fetchEventSections: admin_fetchEventSections,
  fetchDashboard: admin_fetchDashboard,
  fetchOrganizationPayouts: admin_fetchOrganizationPayouts,
  fetchSingleOrganizationPayout: admin_fetchSingleOrganizationPayout,
  fetchPayoutDetailsReport: admin_fetchPayoutDetailsReport,
};

const ANALYTICS = {
  trackEvents: analytics_trackEvents,
};

const KYD_APIS = {
  ADMIN,
  ANALYTICS,
};

export default KYD_APIS;
